import cloneDeep from 'lodash/cloneDeep'
import DesignAPIs from '@/API/design/design-api'

function normalizeThemesResponse(data: any) {
  const estore = data.estore ?? data.mobile ?? []
  const mobile_app = data.mobile_app ?? []
  const ipad = data.ipad ?? []
  return { estore, mobile_app, ipad }
}

function applyDesktopBanners(themes: any[], banners: any[] | null) {
  if (banners == null) return themes
  return themes.map(theme => ({
    ...theme,
    desktop_layout: {
      ...(theme.desktop_layout ?? {}),
      banners: banners.map(file => ({ file })),
    },
  }))
}

function toBannerIds(banners: any[] | null) {
  if (!Array.isArray(banners)) return ''
  const bannerIds = banners.filter(file => file?.id).map(file => file.id)
  return bannerIds.length ? bannerIds : ''
}

export default {
  namespaced: true,

  state: {
    iPadThemes: [],
    estoreThemes: [],
    mobileAppThemes: [],
    themes: {},
    desktopBanners: null,
    themesFetched: false,
    showQrActiveTooltip: false,
    showTabletActiveTooltip: false,
  },

  getters: {
    currentEstoreThemes: (state: any) => state.estoreThemes,
    currentMobileAppThemes: (state: any) => state.mobileAppThemes,
    /** @deprecated Use currentEstoreThemes — kept for backward compatibility */
    currentMobileThemes: (state: any) => state.estoreThemes,
    currentIpadThemes: (state: any) => state.iPadThemes,
    activeMobileTheme: (state: any, getters: any, rootState: any, rootGetters: any) => {
      const activeMobileThemeId = rootGetters['auth/mobileThemeId']
      return state.estoreThemes.find((theme: any) => theme.id === activeMobileThemeId)
    },
    activeIpadTheme: (state: any, getters: any, rootState: any, rootGetters: any) => {
      const activeIpadThemeId = rootGetters['auth/ipadThemeId']
      return state.iPadThemes.find((theme: any) => theme.id === activeIpadThemeId)
    },
    activeCategoryLayoutTheme: (state: any) => state.iPadThemes.find((theme: any) => theme.active)?.categories_layout,
    activeItemsLayoutTheme: (state: any, getters: any, rootState: any, rootGetters: any) => {
      const activeIpadThemeId = rootGetters['auth/ipadThemeId']
      return state.iPadThemes.find((theme: any) => theme.id === activeIpadThemeId)?.items_layout
    },
    showQrActiveTooltip: (state: any) => state.showQrActiveTooltip,
    showTabletActiveTooltip: (state: any) => state.showTabletActiveTooltip,
    desktopBanners: (state: any) => state.desktopBanners,
  },

  mutations: {
    SET_IPAD_THEMES(state: any, themes: Array<any>) {
      state.iPadThemes = themes
    },
    SET_ESTORE_THEMES(state: any, themes: Array<any>) {
      state.estoreThemes = themes
    },
    SET_MOBILE_APP_THEMES(state: any, themes: Array<any>) {
      state.mobileAppThemes = themes
    },
    /** @deprecated Use SET_ESTORE_THEMES */
    SET_Mobile_THEMES(state: any, themes: Array<any>) {
      state.estoreThemes = themes
    },
    SET_THEMES(state: any, options: any) {
      const { branchId, estore, mobile_app, ipad } = options
      state.themes[branchId] = { estore, mobile_app, ipad }
      state.themesFetched = true
    },
    CLEAR_CACHED_THEMES(state: any) {
      state.themes = {}
      state.themesFetched = false
    },
    SET_DESKTOP_BANNERS(state: any, banners: any[] | null) {
      state.desktopBanners = banners == null ? null : cloneDeep(banners)
    },
    SET_SHOW_QR_ACTIVE_TOOLTIP(state: any, show = true) {
      state.showQrActiveTooltip = show
    },
    SET_SHOW_TABLET_ACTIVE_TOOLTIP(state: any, show = true) {
      state.showTabletActiveTooltip = show
    },
  },

  actions: {
    RESET(context: any) {
      context.commit('CLEAR_CACHED_THEMES')
    },
    async FETCH_THEMES(context: any, force: false) {
      if (context.state.themesFetched && !force) {
        return {
          estore: context.state.estoreThemes,
          mobile_app: context.state.mobileAppThemes,
          ipad: context.state.iPadThemes,
        }
      }

      const branchId = context.rootGetters['branches/currentBranchId']
      const { estore, mobile_app, ipad } = await context.dispatch('FETCH_BRANCH_THEMES', branchId)
      context.commit('SET_ESTORE_THEMES', estore)
      context.commit('SET_MOBILE_APP_THEMES', mobile_app)
      context.commit('SET_IPAD_THEMES', ipad)
      return { estore, mobile_app, ipad }
    },
    async FETCH_BRANCH_THEMES(context: any, payload: any) {
      let branchId: any = null
      let force = true
      if (typeof payload === 'number') {
        branchId = payload
      } else {
        branchId = payload.branchId
        force = payload.force ?? false
      }
      if (context.state.themes[branchId] && !force) {
        return context.state.themes[branchId]
      }
      const data = await DesignAPIs.getThemesList()
      const { estore, mobile_app, ipad } = normalizeThemesResponse(data)
      context.commit('SET_THEMES', { branchId, estore, mobile_app, ipad })
      return { estore, mobile_app, ipad }
    },
    async GET_ACTIVE_ITEMS_LAYOUT_THEME(context: any, branchId: number) {
      const { ipad } = await context.dispatch('FETCH_BRANCH_THEMES', branchId)
      const theme = ipad.find((_theme: any) => _theme.active)
      if (!theme) {
        throw Error(`There is no active theme in this branch (${branchId})`)
      }
      const itemsLayout = theme.items_layout
      const categoriesLayout = theme.categories_layout
      return {
        ...categoriesLayout,
        items_layout: itemsLayout?.layout_type ?? itemsLayout?.items_layout ?? 0,
      }
    },
    async GET_ACTIVE_QR_THEME(context: any, payload: number) {
      const { estore } = await context.dispatch('FETCH_BRANCH_THEMES', payload)
      const activeId = context.rootGetters['auth/mobileThemeId']
      const theme = estore.find((_theme: any) => _theme.id === activeId)
      if (!theme) {
        throw Error('There is no active theme in this branch')
      }
      return theme
    },
  },
}
